import { Results, ResultsRecord } from '../../models/unit.model';
import { ElementPropertyName } from '../../models/element-property.model';
import type { Baseline } from '../../models/project.model';
import { multiplyConversionFactors } from '../conversion.helpers';
import {
  getElementPropertyResolvedCountByNameOrId,
  getResolvedCountInPath,
} from '../element-property.helpers';
import { mapObject } from '../object.helpers';
import { guards } from '../typeguards.helpers';
import { MapResultsContext } from './map-results-context.model';

/**
 * Version {@link ElementPropertyName.ContentCoverage} as a 0–1 fraction.
 * Missing or invalid values behave as 1 — the same default as the Content coverage slider.
 */
export const getVersionContentCoverageFraction = (
  version: Baseline,
): number => {
  const count = getElementPropertyResolvedCountByNameOrId(
    version,
    ElementPropertyName.ContentCoverage,
    1,
  );
  if (!guards.is.number(count) || count <= 0) return 1;
  return Math.min(count, 1);
};

/**
 * Version Content coverage as a 0–100 percentage for display (e.g. report donut).
 */
export const getVersionContentCoveragePercent = (version: Baseline): number =>
  getVersionContentCoverageFraction(version) * 100;

/**
 * Scale results by 1 / version Content coverage so child-row totals match
 * Build-view proposal bars. Coverage of 1 (100%, the default) leaves results unchanged.
 */
export const scaleResultsByVersionContentCoverage = (
  results: Results,
  version: Baseline,
): Results => {
  const coverage = getVersionContentCoverageFraction(version);
  if (coverage === 1) return results;
  return multiplyConversionFactors(results, 1 / coverage);
};

/**
 * Apply {@link scaleResultsByVersionContentCoverage} to every row.
 * Returns the same record when coverage is 1.
 */
export const scaleResultsRecordByVersionContentCoverage = (
  record: ResultsRecord,
  version: Baseline,
): ResultsRecord => {
  const coverage = getVersionContentCoverageFraction(version);
  if (coverage === 1) return record;
  const factor = 1 / coverage;
  return mapObject(record, (results) =>
    multiplyConversionFactors(results, factor),
  );
};

/**
 * Scale all results by 1/coverage to compensate for incomplete element recipes.
 * A coverage of 1 (100%, the default) means no adjustment. Resolves coverage along
 * the full ancestor path; the per-element variant is `applyOwnContentCoverage`.
 */
export const mapResultsByContentCoverage = (
  results: Results,
  { path }: MapResultsContext,
): Results => {
  const coverage = getResolvedCountInPath(
    path,
    ElementPropertyName.ContentCoverage,
    1,
  );
  if (!coverage || coverage === 1) return results;
  return multiplyConversionFactors(results, 1 / coverage);
};
